import { Dispatch, RefObject, SetStateAction, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import { getProjectPhaseLabel, ProjectPhase } from "../../../constants/project-phase/ProjectPhase";
import { SelectData } from "../../../models";
import { SearchProjectsRequest, SearchProjectsResponseInvestmentDirectory } from "../../../service/publicquery";
import { searchProjects } from "../../../service/publicquery/PublicQueryService.full";
import { FilterGroups, SortDirection, Status } from "../../../service/Shared";
import { useClickOutside, useDebounce } from "../../../utils";
import { getDefaultInvestmentDirectoryProjectFilterGroups } from "../../../utils/project/projectSearchFilters";
import { DataFilterValueType } from "../../../widget";

type SearchProjectsRequestWithFilters = SearchProjectsRequest & FilterGroups<SearchProjectsRequest["filter"]>;

const PHASE_OPTIONS: SelectData = Object.values(ProjectPhase).map((phase) => ({
  key: phase,
  value: getProjectPhaseLabel(phase),
}));

type DisplayProject = {
  uuid: string;
  imageSource: string | null;
  name: string;
  addressCountryCode: string;
  addressRegion: string | null;
  developerSmallLogoFileUrl: string | null;
  developerDisplayName?: string;
  standardDisplayName?: string;
  cachedAvailableQuantity: number | null;
  quantityType: string | null;
  status: string;
  phase: string;
  projectType: string;
  projectTypeUuid: string;
  areaNetHa: number | null;
  avgPrice: number | null;
  tco2e: number | null;
  landholdings: SearchProjectsResponseInvestmentDirectory["results"][0]["landholdings"];
};

interface InvestmentDirectoryFilter extends DataFilterValueType {
  search?: string | null;
  projectType?: string | null;
  developer?: string | null;
  standard?: string | null;
  phase?: string | null;
  region?: string | null;
  vintage?: number[] | null;
}

interface UseInvestmentDirectoryReturnData {
  currentFilter: InvestmentDirectoryFilter;
  hasActiveFilters: boolean;
  currentSort: string;
  sorting: SelectData;
  projects: DisplayProject[];
  projectTypeOptions: SelectData;
  developerOptions: SelectData;
  standardOptions: SelectData;
  phaseOptions: SelectData;
  regionOptions: SelectData;
  vintageRange?: { min: number; max: number };
  onFilterChange: (name: keyof InvestmentDirectoryFilter, value: string | number[] | null) => void;
  onClearFilters: () => void;
  onSortingChange: (value: string) => void;
  onPrevious: () => void;
  onNext: () => void;
  hasPrevious: boolean;
  hasNext: boolean;
  totalCount: number;
  showVintageSlider: boolean;
  setShowVintageSlider: Dispatch<SetStateAction<boolean>>;
  vintageSelectorRef: RefObject<HTMLDivElement>;
  onClearFilter: (name: keyof InvestmentDirectoryFilter) => void;
}

export const useInvestmentDirectory = (): UseInvestmentDirectoryReturnData => {
  const [aspects, setAspects] = useState<SearchProjectsResponseInvestmentDirectory["aspects"]>();
  const [projects, setProjects] = useState<DisplayProject[]>([]);
  const [pageSize] = useState<number>(8);
  const [totalCount, setTotalCount] = useState<number>(0);
  const [projectTypeOptions, setProjectTypeOptions] = useState<SelectData>([]);
  const [developerOptions, setDeveloperOptions] = useState<SelectData>([]);
  const [standardOptions, setStandardOptions] = useState<SelectData>([]);
  const [phaseOptions, setPhaseOptions] = useState<SelectData>(PHASE_OPTIONS);
  const [regionOptions, setRegionOptions] = useState<SelectData>([]);
  const [vintageRange, setVintageRange] = useState<{ min: number; max: number }>();
  const [sorting] = useState<SelectData>([
    {
      key: "results.dataScore|desc",
      value: "Default",
    },
    {
      key: "results.displayName|asc",
      value: "Name (asc)",
    },
    {
      key: "results.displayName|desc",
      value: "Name (desc)",
    },
    {
      key: "results.developer.displayName|asc",
      value: "Developer (asc)",
    },
    {
      key: "results.developer.displayName|desc",
      value: "Developer (desc)",
    },
    {
      key: "results.standard.displayName|asc",
      value: "Standard (asc)",
    },
    {
      key: "results.standard.displayName|desc",
      value: "Standard (desc)",
    },
  ]);
  const [currentSort, setCurrentSort] = useState<string>(sorting[0].key as string);
  const [currentFilter, setCurrentFilter] = useState<InvestmentDirectoryFilter>({});

  const debounceValue = useDebounce(currentFilter);
  const [searchParams, setSearchParams] = useSearchParams();
  const [startCursor, setStartCursor] = useState<string>();
  const [endCursor, setEndCursor] = useState<string>();
  const [beforeCursor, setBeforeCursor] = useState<string | undefined>(searchParams.get("beforeCursor") || undefined);
  const [afterCursor, setAfterCursor] = useState<string | undefined>(searchParams.get("afterCursor") || undefined);
  const [hasNext, setHasNext] = useState<boolean>(true);
  const [hasPrevious, setHasPrevious] = useState<boolean>(false);

  const [showVintageSlider, setShowVintageSlider] = useState(false);
  const vintageSelectorRef = useRef<HTMLDivElement>(null);
  const latestRequestIdRef = useRef(0);
  const onPrevious = (): void => {
    setBeforeCursor(startCursor);
    setAfterCursor(undefined);
  };
  const onNext = (): void => {
    setBeforeCursor(undefined);
    setAfterCursor(endCursor);
  };

  useEffect(() => {
    currentFilter.search = searchParams.get("search");
    currentFilter.projectType = searchParams.get("projectType");
    currentFilter.developer = searchParams.get("developer");
    currentFilter.standard = searchParams.get("standard");
    currentFilter.phase = searchParams.get("phase");
    currentFilter.region = searchParams.get("region");

    const vintage = searchParams.get("vintage");

    if (vintage) {
      const [calculatedVintageEndDateMin, calculatedVintageEndDateMax] = vintage.split(",");
      currentFilter.vintage = [
        parseInt(calculatedVintageEndDateMin.split("-")[0], 10),
        parseInt(calculatedVintageEndDateMax.split("-")[0], 10),
      ];
    }

    setCurrentFilter({ ...currentFilter });

    const sort = searchParams.get("sort");

    if (sort) setCurrentSort(sort);

    const qsBeforeCursor = searchParams.get("beforeCursor");
    const qsAfterCursor = searchParams.get("afterCursor");

    if (qsBeforeCursor) setBeforeCursor(qsBeforeCursor);
    if (qsAfterCursor) setAfterCursor(qsAfterCursor);
  }, []);

  useEffect(() => {
    if (!aspects) {
      setProjectTypeOptions([]);
      setDeveloperOptions([]);
      setStandardOptions([]);
      setPhaseOptions(PHASE_OPTIONS);
      setRegionOptions([]);
      setVintageRange(undefined);
      return;
    }

    const projectTypeFilterValues = aspects.projectTypes.map((projectTypeItem) => ({
      key: projectTypeItem.uuid,
      value: projectTypeItem.displayName,
    }));

    setProjectTypeOptions(projectTypeFilterValues);

    const developers = aspects.developers.reduce((accumulator, current) => {
      if (!accumulator.some((i) => i.key === current.uuid)) {
        accumulator.push({
          key: current.uuid,
          value: current.displayName,
        });
      }

      return accumulator;
    }, [] as SelectData);

    setDeveloperOptions(developers);

    const standards = aspects.standards.reduce((accumulator, current) => {
      if (!accumulator.some((i) => i.key === current.uuid)) {
        accumulator.push({
          key: current.uuid,
          value: current.displayName,
        });
      }

      return accumulator;
    }, [] as SelectData);

    setStandardOptions(standards);
    setPhaseOptions(PHASE_OPTIONS);

    const regions = aspects.regions
      .flatMap((reg) => reg.addressRegions)
      .filter((value, index, self) => self.indexOf(value) === index)
      .sort((a, b) => a.localeCompare(b))
      .map((reg) => ({
        key: reg,
        value: reg.replaceAll("Лил", "A'"),
      }));

    setRegionOptions(regions);

    if (aspects.calculatedVintageEndDateMin && aspects.calculatedVintageEndDateMax) {
      setVintageRange({
        min: aspects.calculatedVintageEndDateMin?.getFullYear(),
        max: aspects.calculatedVintageEndDateMax?.getFullYear(),
      });
    }
  }, [aspects]);

  const buildInvestmentDirectoryRequest = useCallback(
    (includePagingAndSort: boolean): SearchProjectsRequestWithFilters => {
      const request: SearchProjectsRequestWithFilters = {
        paging: {
          limit: pageSize,
          beforeCursor: null,
          afterCursor: null,
        },
        distinctLandholding: true,
      };

      const search = searchParams.get("search");
      const projectType = searchParams.get("projectType");
      const developer = searchParams.get("developer");
      const standard = searchParams.get("standard");
      const phase = searchParams.get("phase");
      const region = searchParams.get("region");
      const vintage = searchParams.get("vintage");
      const sort = searchParams.get("sort");

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const projectAndDeveloperFilterGroups: any = [];

      if (projectType) {
        projectAndDeveloperFilterGroups.push({
          operator: "or",
          filter: {
            results: {
              projectType: {
                uuid: {
                  operator: "eq",
                  value: projectType,
                },
              },
              landholdings: {
                some: {
                  projectTypes: {
                    some: {
                      uuid: {
                        operator: "eq",
                        value: projectType,
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      if (developer) {
        projectAndDeveloperFilterGroups.push({
          operator: "or",
          filter: {
            results: {
              developer: {
                uuid: {
                  operator: "eq",
                  value: developer,
                },
              },
              landholdings: {
                some: {
                  developers: {
                    some: {
                      uuid: {
                        operator: "eq",
                        value: developer,
                      },
                    },
                  },
                },
              },
            },
          },
        });
      }

      request.filterGroups = getDefaultInvestmentDirectoryProjectFilterGroups();

      if (projectAndDeveloperFilterGroups.length) {
        request.filterGroups = [
          ...request.filterGroups,
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          { operator: "and", filterGroups: projectAndDeveloperFilterGroups } as any,
        ];
      }

      if (search) {
        request.filterGroups = [
          {
            operator: "and",
            filterGroups: [
              ...request.filterGroups,
              {
                operator: "or",
                filterGroups: [
                  {
                    operator: "and",
                    filter: {
                      results: {
                        displayName: { operator: "contains", value: search },
                      },
                    },
                  },
                  {
                    operator: "and",
                    filter: {
                      results: {
                        landholdings: { some: { groupName: { operator: "contains", value: search } } },
                      },
                    },
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                  } as any,
                ],
              },
            ],
          },
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
        ] as any;
      }
      request.filter = {};
      request.filter.results = {};

      if (standard) {
        request.filter.results.standard = {
          uuid: {
            operator: "in",
            value: [standard],
          },
        };
      }

      if (phase) {
        request.filter.results.cachedPhase = {
          operator: "eq",
          value: phase,
        };
      }

      if (region) {
        request.filter.results.addressRegion = {
          operator: "eq",
          value: region,
        };
      }

      if (vintage) {
        const [calculatedVintageEndDateMin, calculatedVintageEndDateMax] = vintage.split(",");

        const [minYear, minMonth, minDay] = calculatedVintageEndDateMax.split("-");
        request.filter.results.calculatedVintageEndDateMin = {
          operator: "lte",
          value: new Date(parseInt(minYear, 10), parseInt(minMonth, 10), parseInt(minDay, 10)),
        };

        const [maxYear, maxMonth, maxDay] = calculatedVintageEndDateMin.split("-");
        request.filter.results.calculatedVintageEndDateMax = {
          operator: "gte",
          value: new Date(parseInt(maxYear, 10), parseInt(maxMonth, 10), parseInt(maxDay, 10)),
        };
      }

      if (includePagingAndSort) {
        const sortOrder = sort || currentSort;
        if (sortOrder) {
          const [key, direction] = sortOrder.split("|");

          request.sort = [
            {
              key: key as never,
              direction: direction as SortDirection,
            },
            {
              key: "results.id",
              direction: "asc",
            },
          ];
        }

        if (beforeCursor) {
          request.paging.beforeCursor = beforeCursor;
        } else if (afterCursor) {
          request.paging.afterCursor = afterCursor;
        }
      }

      return request;
    },
    [afterCursor, beforeCursor, currentSort, pageSize, searchParams]
  );

  const loadProjectsAndAspects = useCallback(async () => {
    const requestId = latestRequestIdRef.current + 1;
    latestRequestIdRef.current = requestId;

    const resultsRequest = buildInvestmentDirectoryRequest(true);
    const aspectsRequest = buildInvestmentDirectoryRequest(false);

    const resultsPromise = searchProjects(resultsRequest, "investmentDirectoryResults");
    const aspectsPromise = searchProjects(aspectsRequest, "investmentDirectoryAspects");

    const resultsResponse = await resultsPromise;

    if (latestRequestIdRef.current !== requestId) {
      return;
    }

    if (resultsResponse.status === Status.Success && resultsResponse.data) {
      setTotalCount(resultsResponse.data.paging.total || 0);
      setStartCursor(resultsResponse.data.paging.startCursor);
      setEndCursor(resultsResponse.data.paging.endCursor);
      setHasPrevious(resultsResponse.data.paging.hasPreviousPage);
      setHasNext(resultsResponse.data.paging.hasNextPage);
      setProjects(
        resultsResponse.data.results?.map((d) => ({
          uuid: d.uuid,
          name: d.displayName,
          imageSource:
            d?.listing?.content?.images && d?.listing?.content?.images[0]
              ? d.listingFiles?.find((el) => el.uuid === d?.listing?.content?.images[0])?.file.url
              : "",
          addressCountryCode: d.addressCountryCode || "",
          addressRegion: d.addressRegion,
          developerSmallLogoFileUrl: d.developer?.files ? d.developer.files[0]?.file.url : "",
          developerDisplayName: d.developer?.displayName || undefined,
          standardDisplayName: d.standard?.displayName || undefined,
          cachedAvailableQuantity: d.cachedUnitAvailableForSale || 0,
          quantityType: d.cachedQuantityType,
          phase: getProjectPhaseLabel(d.cachedPhase),
          status: d.status,
          projectType: d.projectType?.displayName || "",
          projectTypeUuid: d.projectType?.uuid || "",
          areaNetHa: d.areaNetHa,
          avgPrice: d.calculatedIssuancePriceAvg,
          tco2e: d.tco2e,
          landholdings: d.landholdings,
        })) || []
      );
    }

    const aspectsResponse = await aspectsPromise;

    if (latestRequestIdRef.current !== requestId) {
      return;
    }

    if (aspectsResponse.status === Status.Success && aspectsResponse.data?.aspects) {
      setAspects(aspectsResponse.data.aspects);
      return;
    }

    setAspects(undefined);
  }, [buildInvestmentDirectoryRequest]);

  useEffect(() => {
    loadProjectsAndAspects();
  }, [loadProjectsAndAspects]);

  useEffect(() => {
    if (!aspects) return;

    if (currentFilter) {
      const { search, projectType, developer, standard, phase, region, vintage } = currentFilter;

      if (search) {
        searchParams.set("search", search);
      } else searchParams.delete("search");

      if (projectType) {
        searchParams.set("projectType", projectType);
      } else searchParams.delete("projectType");

      if (developer) {
        searchParams.set("developer", developer);
      } else searchParams.delete("developer");

      if (standard) {
        searchParams.set("standard", standard);
      } else searchParams.delete("standard");

      if (phase) {
        searchParams.set("phase", phase);
      } else searchParams.delete("phase");

      if (region) {
        searchParams.set("region", region);
      } else searchParams.delete("region");

      if (vintage) {
        searchParams.set(
          "vintage",
          `${vintage[0].toString()}-${aspects.calculatedVintageEndDateMin.getMonth()}-${aspects.calculatedVintageEndDateMin.getDay()},` +
            `${vintage[1].toString()}-${aspects.calculatedVintageEndDateMax.getMonth()}-${aspects.calculatedVintageEndDateMax.getDay()}`
        );
      } else {
        searchParams.delete("vintage");
      }
    }

    if (currentSort && currentSort !== sorting[0].key) {
      searchParams.set("sort", currentSort);
    } else searchParams.delete("sort");

    if (beforeCursor) searchParams.set("beforeCursor", beforeCursor);
    else searchParams.delete("beforeCursor");

    if (afterCursor) searchParams.set("afterCursor", afterCursor);
    else searchParams.delete("afterCursor");

    setSearchParams(searchParams);
  }, [debounceValue, currentSort, beforeCursor, afterCursor]);

  const onFilterChange = (name: keyof InvestmentDirectoryFilter, value: string | number[] | null): void => {
    setCurrentFilter({
      ...currentFilter,
      [name]: value,
    });
    setBeforeCursor(undefined);
    setAfterCursor(undefined);
  };

  const onClearFilters = (): void => {
    setCurrentFilter({});
    setBeforeCursor(undefined);
    setAfterCursor(undefined);
  };

  const onSortingChange = (value: string): void => {
    setCurrentSort(value);
    setBeforeCursor(undefined);
    setAfterCursor(undefined);
  };

  useClickOutside(vintageSelectorRef, () => {
    setShowVintageSlider(false);
  });

  const onClearFilter = (name: keyof InvestmentDirectoryFilter): void => {
    setCurrentFilter({
      ...currentFilter,
      [name]: null,
    });
    setBeforeCursor(undefined);
    setAfterCursor(undefined);
    if (name === "vintage") {
      setShowVintageSlider(false);
    }
  };

  const hasActiveFilters = useMemo(
    () =>
      Boolean(
        currentFilter.search ||
          currentFilter.projectType ||
          currentFilter.developer ||
          currentFilter.standard ||
          currentFilter.phase ||
          currentFilter.region ||
          (currentFilter.vintage && currentFilter.vintage.length)
      ),
    [currentFilter]
  );

  return {
    currentFilter,
    hasActiveFilters,
    totalCount,
    currentSort,
    sorting,
    projects,
    projectTypeOptions,
    developerOptions,
    standardOptions,
    phaseOptions,
    regionOptions,
    vintageRange,
    onFilterChange,
    onClearFilters,
    onSortingChange,
    onPrevious,
    onNext,
    hasPrevious,
    hasNext,
    vintageSelectorRef,
    showVintageSlider,
    setShowVintageSlider,
    onClearFilter,
  };
};
